import { CircleCheck, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { FormProvider, Path, useForm } from 'react-hook-form';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
} from '@column/ui';

import { AddressFields, FormFieldProps, NotificationList, RenderFields } from '~/components';
import { SectionField } from '~/components/RenderFields';
import { useRoutingNumberInstitution } from '~/components/RoutingNumberInput';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { COUNTERPARTY_ACCOUNT_TYPE_OPTIONS, coerceCounterpartyAccountType } from '~/lib/counterparties/accountType';
import { CounterpartyRepository, CreateCounterparty } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';

interface CounterpartyForm extends CreateCounterparty {}

type Field = SectionField<CounterpartyForm, undefined>;
type Option = { label: string; value: string };

const ROUTING_NUMBER_TYPE_OPTIONS: Option[] = [
  { label: 'ABA', value: 'aba' },
  { label: 'BIC', value: 'bic' },
];

const BENEFICIARY_TYPE_OPTIONS: Option[] = [
  { label: 'Business', value: 'business' },
  { label: 'Nonprofit', value: 'non_profit' },
  { label: 'Individual', value: 'individual' },
  { label: 'Sole proprietor', value: 'sole_proprietor' },
];

type FieldOptions = Pick<FormFieldProps<CounterpartyForm>, 'rules' | 'hide'>;

const textField = (
  id: Path<CounterpartyForm>,
  label: string,
  placeholder: string,
  extra: FieldOptions = {}
): Field => ({
  id,
  label,
  ...extra,
  children: ({ value, onChange, onBlur }, { error }) => (
    <Input
      value={String(value ?? '')}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      aria-invalid={!!error}
    />
  ),
});

const selectField = (
  id: Path<CounterpartyForm>,
  label: string,
  options: Option[],
  placeholder: string,
  extra: FieldOptions = {}
): Field => ({
  id,
  label,
  ...extra,
  children: ({ value, onChange }, { error }) => (
    <Select value={value ? String(value) : undefined} onValueChange={onChange}>
      <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ),
});

const RoutingNumberLookupInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  invalid?: boolean;
}> = ({ value, onChange, onBlur, invalid }) => {
  const { institution, checkRoutingNumber } = useRoutingNumberInstitution();
  const institutionName = institution?.fullName ?? institution?.shortName;

  return (
    <>
      <Input
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          checkRoutingNumber(event.target.value);
        }}
        onBlur={onBlur}
        placeholder="Routing number"
        aria-invalid={invalid}
      />
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200',
          institution ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <p className="mt-1.5 flex items-center gap-1 truncate text-xs leading-5 font-medium text-seafoam-600">
            <CircleCheck className="size-4 shrink-0" />
            {institutionName}
          </p>
        </div>
      </div>
    </>
  );
};

export interface ModalCounterpartyProps {
  isOpen: boolean;
  onClose: () => void;
  callback?: (counterparty: any) => void;
  type?: string;
}

export const ModalCounterparty: React.FC<ModalCounterpartyProps> = ({ isOpen, onClose, callback, type: modalType }) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const [type, setType] = useState<string>(modalType || '');

  const formMethods = useForm<CounterpartyForm>({
    defaultValues: {
      description: '',
      routingNumber: '',
      routingNumberType: undefined,
      accountNumber: '',
      accountType: 'checking',
      wireDrawdownAllowed: false,
      wire: {
        beneficiaryName: '',
        beneficiaryEmail: '',
        beneficiaryPhone: '',
        beneficiaryLegalId: '',
        beneficiaryType: undefined,
        localBankCode: '',
        localAccountNumber: '',
        beneficiaryAddress: {
          line1: '',
          line2: '',
          city: '',
          countryCode: '',
          postalCode: '',
          state: '',
        },
      },
    },
  });

  const { handleSubmit, reset } = formMethods;
  const { handleSubmitErrors } = useFormErrorHandler();

  const onSubmit = (data: CounterpartyForm) => {
    const payload = { ...data, accountType: coerceCounterpartyAccountType(data.accountType) ?? 'checking' };
    if (type !== 'wire' && type !== 'realtime') {
      delete payload.wire;
    }

    CounterpartyRepository.create(payload)
      .then((response) => {
        if (callback) {
          callback(response);
        }
        onClose();
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  };

  useEffect(() => {
    if (isOpen) {
      reset();
      setType(modalType || '');
    }
  }, [isOpen, reset, modalType]);

  const isWire = type === 'wire';
  const hasBeneficiary = isWire || type === 'realtime';

  const fields: Field[] = [
    textField('description', 'Description', 'Description'),
    {
      id: 'routingNumber',
      label: 'Routing Number',
      rules: { required: true },
      children: ({ value, onChange, onBlur }, { error }) => (
        <RoutingNumberLookupInput value={String(value ?? '')} onChange={onChange} onBlur={onBlur} invalid={!!error} />
      ),
    },
    selectField('routingNumberType', 'Routing Number Type', ROUTING_NUMBER_TYPE_OPTIONS, 'Select type'),
    textField('accountNumber', 'Account Number', 'Account number', { rules: { required: true } }),
    selectField('accountType', 'Account Type', COUNTERPARTY_ACCOUNT_TYPE_OPTIONS, 'Select type'),
    textField('wire.beneficiaryName', 'Beneficiary Name', 'Beneficiary name', { hide: !hasBeneficiary }),
    {
      id: 'wireDrawdownAllowed',
      label: 'Wire Drawdown Allowed',
      hide: !isWire,
      children: ({ value, onChange }) => (
        <div className="flex @tablet/form-field:mt-3">
          <Switch aria-label="Wire Drawdown Allowed" checked={Boolean(value)} onCheckedChange={onChange} />
        </div>
      ),
    },
    textField('wire.beneficiaryEmail', 'Beneficiary Email', 'Beneficiary email', { hide: !isWire }),
    textField('wire.beneficiaryPhone', 'Beneficiary Phone', 'Beneficiary phone', { hide: !isWire }),
    textField('wire.beneficiaryLegalId', 'Beneficiary Legal ID', 'Beneficiary legal ID', { hide: !isWire }),
    selectField('wire.beneficiaryType', 'Beneficiary Type', BENEFICIARY_TYPE_OPTIONS, 'Select type', {
      hide: !isWire,
    }),
    textField('wire.localBankCode', 'Local Bank Code', 'Local bank code', { hide: !isWire }),
    textField('wire.localAccountNumber', 'Local Account Number', 'Local account number', { hide: !isWire }),
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent overlayScroll className={cn('gap-3', isWire ? 'sm:max-w-3xl' : 'sm:max-w-xl')}>
        <DialogHeader className="-mx-6 -mt-2 border-b border-gray-100 px-6 pb-4 dark:border-white/[0.08]">
          <DialogTitle>New Counterparty</DialogTitle>
        </DialogHeader>

        {/* Inside the dialog so errors stack above its overlay. */}
        <NotificationList display="popup" spacing="bottom" className="!m-0" />

        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit, handleSubmitErrors)} className="-mx-6 -mt-2 [--page-margin:24px]">
            {RenderFields<CounterpartyForm>({ sections: [{ fields }] })}
            {isWire && <AddressFields name="wire.beneficiaryAddress" heading="Beneficiary Address" />}
          </form>
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={() => handleSubmit(onSubmit, handleSubmitErrors)()}>
            <Plus />
            Create &amp; Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
